import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';

export interface NormsMeta {
  version: string;
  updated: string;
  ttl_days: number;
  /** Current pipe-fab rate-book contract version; used to invalidate its cached book. */
  pipeFabVersion?: string;
}

const DB_NAME = 'nest-norms-cache';
const STORE_NAME = 'responses';
const META_KEY = '__meta__';

@Injectable({ providedIn: 'root' })
export class NormsCacheService {
  private http = inject(HttpClient);
  private dbPromise: Promise<IDBDatabase> | null = null;
  /** Derived from nestApiUrl so it points to the correct host in every environment. */
  private readonly metaUrl = environment.nestApiUrl.replace('nest-lookup', 'norms-meta');

  /** Populated after initCache() completes — readable by any component/service. */
  meta: NormsMeta | null = null;

  /**
   * Resolves when cache validation is complete (or after 8 s if the Function
   * is cold-starting). Components can optionally await this before first use,
   * but the app no longer blocks on it at bootstrap.
   */
  readonly ready: Promise<void>;

  constructor() {
    // Kick off validation immediately — fire-and-forget from the app's perspective.
    // Race against a timeout so a cold Function start never stalls the UI.
    const timeout = new Promise<void>(resolve => setTimeout(resolve, 8_000));
    this.ready = Promise.race([this.initCache(), timeout]);
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE_NAME)) {
          req.result.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        this.dbPromise = null;
        reject(req.error);
      };
    });
    return this.dbPromise;
  }

  /**
   * Called at app startup. Fetches /api/norms-meta, compares to stored meta,
   * and clears the cache if the model version changed or TTL has expired.
   */
  async initCache(): Promise<void> {
    try {
      const meta = await firstValueFrom(
        this.http.get<NormsMeta>(this.metaUrl)
      );
      const stored = await this.get(META_KEY) as NormsMeta | null;

      if (stored && stored.version === meta.version) {
        const updatedDate = new Date(meta.updated);
        const daysSinceUpdate =
          (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceUpdate <= meta.ttl_days) {
          // Cache is still valid — refresh the stored meta and return
          await this.storeMeta(meta);
          this.meta = meta;
          return;
        }
      }

      // Version changed or TTL expired — wipe and re-store meta
      await this.clearCache();
      await this.storeMeta(meta);
      this.meta = meta;
    } catch {
      // Non-fatal: app works without cache
    }
  }

  async get(key: string): Promise<any | null> {
    try {
      const db = await this.openDb();
      return await new Promise<any>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
      });
    } catch {
      return null;
    }
  }

  async set(key: string, value: any): Promise<void> {
    try {
      const db = await this.openDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const req = tx.objectStore(STORE_NAME).put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch {
      // Non-fatal: continue without caching this entry
    }
  }

  async clearCache(): Promise<void> {
    try {
      const db = await this.openDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const req = tx.objectStore(STORE_NAME).clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch {
      // Non-fatal
    }
  }

  async storeMeta(meta: NormsMeta): Promise<void> {
    await this.set(META_KEY, meta);
  }
}
